import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    reason: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchSlots();
  }, [currentMonth]);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
	const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59);
      
      const response = await fetch(
        `/api/slots?start=${startDate.toISOString()}&end=${endDate.toISOString()}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch slots');
      
      const data = await response.json();
      setSlots(data);
      setError('');
    } catch (err) {
      setError('Failed to load available slots. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSlot) {
      setError('Please select a time slot');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/appointments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          start: selectedSlot.start,
          end: selectedSlot.end
        }),
      });
      
      if (!response.ok) throw new Error('Failed to book appointment');
      
      setSuccess(true);
      setFormData({ name: '', email: '', reason: '' });
      setSelectedSlot(null);
      fetchSlots();
    } catch (err) {
      setError('Failed to book appointment. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const groupSlotsByDate = () => {
    const grouped = {};
    
    slots.forEach(slot => {
      const date = new Date(slot.start).toDateString();
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });
    
    return grouped;
  };

  const changeMonth = (offset) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + offset);
    setCurrentMonth(newMonth);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const groupedSlots = groupSlotsByDate();
  const dates = Object.keys(groupedSlots).sort((a, b) => new Date(a) - new Date(b));

  if (success) {
    return (
      <div className="App">
        <div className="success-message">
          <h2>✓ Appointment Registered!</h2>
          <p>You will receive an email shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Book an Appointment</h1>
      </header>

      <div className="container">
        <div className="calendar-section">
          <div className="month-navigation">
            <button onClick={() => changeMonth(-1)}>&larr; Previous</button>
            <h2>
              {currentMonth.toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </h2>
            <button onClick={() => changeMonth(1)}>Next &rarr;</button>
          </div>

          {loading && <div className="loading">Loading available slots...</div>}
          
          {error && <div className="error">{error}</div>}

          {!loading && dates.length === 0 && (
            <div className="no-slots">No available slots this month</div>
          )}

          <div className="slots-list">
            {dates.map(date => (
              <div key={date} className="date-group">
                <h3>{formatDate(date)}</h3>
                <div className="time-slots">
                  {groupedSlots[date].map((slot, index) => (
                    <button
                      key={index}
                      className={`time-slot ${selectedSlot === slot ? 'selected' : ''}`}
                      onClick={() => setSelectedSlot(slot)}
                    >
                      {formatTime(slot.start)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <h2>Your Information</h2>
          
          {selectedSlot && (
            <div className="selected-slot-info">
              <strong>Selected:</strong> {formatDate(selectedSlot.start)} at {formatTime(selectedSlot.start)}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="reason">Reason for Appointment *</label>
              <textarea
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                rows="4"
                required
              />
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={loading || !selectedSlot}
            >
              {loading ? 'Booking...' : 'Book Appointment'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default App;
